import {
  DetailedServiceBookingData,
  DetailedServiceBookableEquipment,
  DetailedServiceCatering,
  OrderRequestData
} from "../Domain/RemoteOrderRequests.types";
import { isResourceOrderRequest } from "./resolveOrderRequestBookingType.function";

function normalizeEquipmentLine(
  equipment: DetailedServiceBookableEquipment
): DetailedServiceBookableEquipment {
  return {
    ...equipment,
    bookingLeadTime: equipment.bookingLeadTime ?? 0,
    costPerHour: equipment.costPerHour ?? 0
  };
}

export function finalizeOrderRequestEquipmentDetail(
  data: DetailedServiceBookingData
): DetailedServiceBookingData {
  return {
    ...data,
    equipments: data.equipments.map(normalizeEquipmentLine)
  };
}

type OrderRequestListRow = Pick<
  OrderRequestData,
  "resourceKind" | "numberOfCaterings" | "numberOfBookableEquipments"
>;

export function createStubResourceEquipmentLine(
  data: DetailedServiceBookingData
): DetailedServiceBookableEquipment {
  const serviceId = data.bookingInventoryId ?? data.id;
  const quantity = data.numberOfBookableEquipments > 0 ? data.numberOfBookableEquipments : 1;

  return {
    id: serviceId,
    serviceId,
    name: data.inventoryName,
    image: null,
    category: "",
    setupTime: 0,
    quantity,
    status: data.status,
    deliveryStatus: data.deliveryStatus,
    description: ""
  };
}

export function resolveResourceCateringOrderedQty(
  data: Pick<DetailedServiceBookingData, "numberOfCaterings" | "caterings">
): number {
  const line = data.caterings?.[0];
  const fromLine = line?.orderedQty ?? line?.quantity;
  if (fromLine != null && fromLine > 0) {
    return fromLine;
  }

  if (data.numberOfCaterings > 0) {
    return data.numberOfCaterings;
  }

  return 1;
}

export function createStubResourceCateringLine(
  data: DetailedServiceBookingData
): DetailedServiceCatering {
  const serviceId = data.bookingInventoryId ?? data.id;
  const orderedQty = resolveResourceCateringOrderedQty(data);

  return {
    id: serviceId,
    serviceId,
    name: data.inventoryName,
    image: null,
    description: "",
    pricePerUnit: 0,
    quantity: orderedQty,
    orderedQty,
    availableQty: orderedQty,
    status: data.status,
    deliveryStatus: data.deliveryStatus,
    setupTime: 0,
    bookingLeadTime: 0
  };
}

export function mergeOrderRequestDetailWithListRow(
  data: DetailedServiceBookingData,
  listRow?: OrderRequestListRow | null
): DetailedServiceBookingData {
  if (!listRow) {
    return data;
  }

  return {
    ...data,
    resourceKind: data.resourceKind ?? listRow.resourceKind,
    numberOfCaterings: data.numberOfCaterings || listRow.numberOfCaterings,
    numberOfBookableEquipments:
      data.numberOfBookableEquipments || listRow.numberOfBookableEquipments
  };
}

export function normalizeOrderRequestDetailData(
  data: DetailedServiceBookingData,
  listRow?: OrderRequestListRow | null,
  options?: { finalizeEquipmentDefaults?: boolean }
): DetailedServiceBookingData {
  const merged = mergeOrderRequestDetailWithListRow(data, listRow);
  const isResourceOrder = isResourceOrderRequest(merged);
  const resourceKind = merged.resourceKind;

  let caterings = merged.caterings ?? [];
  let equipments = merged.equipments ?? [];

  if (isResourceOrder && resourceKind === "equipment" && equipments.length === 0) {
    equipments = [createStubResourceEquipmentLine(merged)];
  }

  if (isResourceOrder && resourceKind === "catering" && caterings.length === 0) {
    caterings = [createStubResourceCateringLine(merged)];
  }

  const normalizedCaterings = caterings.map(cat => {
    if (!isResourceOrder || resourceKind !== "catering") {
      return cat;
    }

    const orderedQty = cat.orderedQty ?? cat.quantity ?? resolveResourceCateringOrderedQty(merged);
    const availableQty = cat.availableQty ?? orderedQty;

    return {
      ...cat,
      orderedQty,
      availableQty,
      quantity: cat.quantity ?? orderedQty
    };
  });

  const finalizeEquipmentDefaults = options?.finalizeEquipmentDefaults ?? true;
  const equipmentLines = finalizeEquipmentDefaults
    ? equipments.map(normalizeEquipmentLine)
    : equipments;

  return {
    ...merged,
    caterings: normalizedCaterings,
    equipments: equipmentLines,
    roomLayouts: merged.roomLayouts ?? []
  };
}
